import { backendApi } from "./backendApi.rtkq";
import {
  getCourtScraperStats,
  listCourtScraperDocuments,
  getCourtDocumentMedia,
  getCourtFilterOptions,
} from "@/lib/firebaseFunctions";

export interface CourtScraperKpis {
  total_documents: number;
  downloaded_pdfs: number;
  pending_pdfs: number;
  failed_pdfs: number;
  pages_split: number;
  total_consolidated: number;
  awaiting_nlp: number;
  nlp_pending: number;
  nlp_running: number;
  nlp_completed: number;
  nlp_failed: number;
  nlp_skipped: number;
  nlp_noise: number;
}

export interface CourtDistribution {
  court_name: string;
  document_count: number;
}

export interface DecisionTypeDistribution {
  decision_type_name: string;
  document_count: number;
}

export interface CourtScraperStatsResponse {
  kpis: CourtScraperKpis;
  courts: CourtDistribution[];
  decisionTypes: DecisionTypeDistribution[];
  refreshedAt: string;
}

export interface DocumentRow {
  id: string;
  netLawDocumentId: number;
  filename: string;
  downloadStatus: string;
  fileSizeKB: number | null;
  splitStatus: string;
  splitPageCount: number | null;
  consolidationStatus: string;
  consolidatedGcsPath: string | null;
  gcsBucket: string | null;
  gcsPath: string | null;
  createdAt: string;
  updatedAt: string;
  decisionId: string | null;
  netLawDecisionId: number | null;
  decisionTypeName: string | null;
  decisionSignatureDate: string | null;
  caseId: string | null;
  caseDisplayIdentifier: string | null;
  caseName: string | null;
  courtId: string | null;
  courtName: string | null;
  courtType: string | null;
  judgeName: string | null;
  nlpJobId: string | null;
  nlpStatus: string | null;
  nlpError: string | null;
  nlpCompletedAt: string | null;
}

export interface ListDocumentsParams {
  page?: number;
  pageSize?: number;
  courtName?: string;
  decisionType?: string;
  downloadStatus?: string;
  consolidationStatus?: string;
  nlpStatus?: string;
  search?: string;
}

export interface ListDocumentsResponse {
  items: DocumentRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DocumentMediaParams {
  gcsBucket?: string | null;
  gcsPath?: string | null;
  consolidatedGcsPath?: string | null;
  filename: string;
  caseName?: string | null;
}

export interface DocumentMediaResponse {
  pdfSignedUrl: string | null;
  pdfBase64?: string | null;
  extractedJson: any | null;
  filename: string;
  caseName: string | null;
}

export interface FilterOptionsResponse {
  courts: string[];
  decisionTypes: string[];
}

export const courtScraperApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    getCourtScraperStats: builder.query<CourtScraperStatsResponse, void>({
      queryFn: async () => {
        try {
          const res = await getCourtScraperStats();
          const responseData = res.data as { success: boolean; data: CourtScraperStatsResponse };
          return { data: responseData.data };
        } catch (error: any) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error?.message || "Failed to fetch court scraper stats",
            },
          };
        }
      },
      providesTags: ["CourtScraper"],
    }),

    listCourtScraperDocuments: builder.query<ListDocumentsResponse, ListDocumentsParams>({
      queryFn: async (params) => {
        try {
          const res = await listCourtScraperDocuments(params);
          const responseData = res.data as { success: boolean; data: ListDocumentsResponse };
          return { data: responseData.data };
        } catch (error: any) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error?.message || "Failed to list court documents",
            },
          };
        }
      },
      providesTags: ["CourtScraper"],
    }),

    getCourtDocumentMedia: builder.query<DocumentMediaResponse, DocumentMediaParams>({
      queryFn: async (params) => {
        try {
          const res = await getCourtDocumentMedia(params);
          const responseData = res.data as { success: boolean; data: DocumentMediaResponse };
          return { data: responseData.data };
        } catch (error: any) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error?.message || "Failed to fetch document media",
            },
          };
        }
      },
    }),

    getCourtFilterOptions: builder.query<FilterOptionsResponse, void>({
      queryFn: async () => {
        try {
          const res = await getCourtFilterOptions();
          const responseData = res.data as { success: boolean; data: FilterOptionsResponse };
          return { data: responseData.data };
        } catch (error: any) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error?.message || "Failed to fetch filter options",
            },
          };
        }
      },
      providesTags: ["CourtScraper"],
    }),
  }),
});

export const {
  useGetCourtScraperStatsQuery,
  useListCourtScraperDocumentsQuery,
  useGetCourtDocumentMediaQuery,
  useGetCourtFilterOptionsQuery,
} = courtScraperApi;
